import type * as React from "react";
import { type FC, memo, type ReactElement, useMemo } from "react";
import { type RootState } from "~/app/store";
import { useDispatch, useSelector } from "react-redux";
import { type LinearStep, type LinearSubstep } from "~/features/configurator/Linear/models";
import SystemLayoutSubstep from "~/features/configurator/Linear/components/Substeps/SystemLayoutSubstep";
import { nextStep, previousStep, resetCurrentStep } from "~/features/configurator/Linear/redux/actions";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { GenericTileSubstep } from "~/features/configurator/common/components/GenericTileSubstep/GenericTileSubstep";
import LengthSubstep from "~/features/configurator/Linear/components/Substeps/LengthSubstep";
import ClosureSubstep from "~/features/configurator/Linear/components/Substeps/ClosureSubstep";
import LubricationSubstep from "~/features/configurator/Linear/components/Substeps/LubricationSubstep";
import GuidewaySetStep from "~/features/configurator/Linear/components/Substeps/GuidewaySetStep/GuidewaySetStep";
import LinearReset from "~/features/configurator/Linear/components/Substeps/LinearReset";
import CarriageDetailsSubstep from "~/features/configurator/Linear/components/Substeps/CarriageDetailsSubstep/CarriageDetailsSubstep";
import { IconType } from "~/common/components/icon/IconType";
import { IconSize } from "~/common/components/icon/IconSize";
import { Icon } from "~/common/components/icon/Icon";
import { ConfigurationSummarySubstep } from "~/features/configurator/common/components/ConfigurationSummarySubstep";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import CarriageLocatingFaceSubstep from "~/features/configurator/Carriage/components/CarriageLocatingFaceSubstep";
import LinearLocatingFaceSubstep from "~/features/configurator/Linear/components/Substeps/LinearLocatingFaceSubstep";
import RailLocatingFaceSubstep from "~/features/configurator/Rail/components/RailLocatingFaceSubstep";
import ConfigureCarriageSubstep from "~/features/configurator/Carriage/components/ConfigureCarriageSubstep/ConfigureCarriageSubstep";
import { LinearTranslation } from "~/features/configurator/common/components/LinearTranslation";
import ConfigureRailSubstep from "~/features/configurator/Rail/components/ConfigureRailSubstep/ConfigureRailSubstep";
import { Button, Stack } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { ShaftLengthSubstep } from "~/features/configurator/Shaft/substeps/ShaftLengthSubstep";
import { ShaftToleranceSubstep } from "~/features/configurator/Shaft/substeps/ShaftToleranceSubstep";
import { ShaftMachiningSubstep } from "~/features/configurator/Shaft/substeps/ShaftMachiningSubstep/ShaftMachiningSubstep";
import { useNextStepName, usePreviousStepName } from "~/features/configurator/common/useRenderStepName";
import { MetricShaftMachiningExtraSubstep } from "~/features/configurator/Shaft/metric/components/MetricShaftMachiningExtraSubstep/MetricShaftMachiningExtraSubstep";
import { Alert } from "~/common/mui/Alert/Alert";
import KLLTGuidewaySetStep from "~/features/configurator/Linear/components/Substeps/GuidewaySetStep/KLLTGuidewaySetStep";
import KLLTCarriageDetailsSubstep from "~/features/configurator/Linear/components/Substeps/CarriageDetailsSubstep/KLLTCarriageDetailsSubstep";

type CsticBuilder = (
  data: LinearSubstep,
  index: number,
  totalAmount: number,
  configuratorType: ConfiguratorType,
) => ReactElement | null;

const Step: FC = () => {
  const steps = useSelector(getSteps);
  const currentStepIndex = useSelector(getCurrentStepIndex);
  const elementDefs = useSelector(getElementDefs);
  const configuratorType = useSelector((state: RootState) => state.linearConfigurator.type!);

  const currentStepData = steps[currentStepIndex];

  const { reset, next: nextClick, previous: previousClick } = useControlButtons();
  const { t } = useTranslation();

  const errorCount = currentStepData.substeps
    .map((substep) => elementDefs[substep])
    .filter((substep) => substep.isLenientInvalid).length;

  const resetLabel = <LinearTranslation translationKey={currentStepData.resetLabel} />;

  // const showConfirmation = currentStepIndex > 1;
  const showConfirmation = currentStepData.showResetConfirmation;

  const nextStepName = useNextStepName();
  const previousStepName = usePreviousStepName();

  return (
    <>
      {currentStepIndex === 6 && (
        <div className="scha__linear-configurator__upper-buttons-area">
          <LinearReset
            label={resetLabel}
            onReset={reset}
            showConfirmation={showConfirmation}
            popupMessage={t("page.linear.fullReset.message")}
          />
        </div>
      )}
      <div className="scha__linear-configurator__semi-steps">
        {createSubsteps(currentStepData, elementDefs, configuratorType)}
      </div>
      {errorCount > 0 && (
        <Alert className="mt-30 w-100" severity="error">
          {t("page.linear.invalidSelection", { count: errorCount })}
        </Alert>
      )}
      {currentStepIndex + 1 !== steps.length && ( //don't show on last step (summary)
        <div className="scha__linear-configurator__buttons-area">
          <LinearReset
            label={resetLabel}
            onReset={reset}
            showConfirmation={showConfirmation}
            popupMessage={t("page.linear.fullReset.message")}
          />

          <Stack direction={"row"} gap={4}>
            {currentStepIndex > 0 && (
              <Button
                onClick={previousClick}
                color={"secondary"}
                startIcon={<Icon type={IconType.ChevronLeft} size={IconSize.FONT_20} />}
              >
                {previousStepName}
              </Button>
            )}
            {currentStepIndex + 1 < steps.length && (
              <Button onClick={nextClick} endIcon={<Icon type={IconType.ChevronRight} size={IconSize.FONT_20} />}>
                {nextStepName}
              </Button>
            )}
          </Stack>
        </div>
      )}
    </>
  );
};

export default memo(Step);

const getSteps = (state: RootState) => state.linearConfigurator.steps;
const getCurrentStepIndex = (state: RootState) => state.linearConfigurator.currentStep;

const getElementDefs = (state: RootState) => state.linearConfigurator.substeps;

function useControlButtons() {
  const dispatch = useDispatch();
  return useMemo(() => {
    return {
      reset: (e?: React.MouseEvent) => {
        e?.preventDefault();
        dispatch(resetCurrentStep());
      },
      next: () => dispatch(nextStep()),
      previous: () => dispatch(previousStep()),
    };
  }, [dispatch]);
}

function createSubsteps(stepData: LinearStep, elementDefs: Dict<LinearSubstep>, configuratorType: ConfiguratorType) {
  const builders = stepData.substeps
    .map((elementType) => {
      return {
        elementType,
        builder: getSubstepBuilder(elementType),
      };
    })
    .filter((obj) => !!obj.builder && elementDefs[obj.elementType].isVisible);

  const csticAmount = builders.length;

  return builders.map(({ elementType, builder }, index) =>
    builder!(elementDefs[elementType], index + 1, csticAmount, configuratorType),
  );
}

function getSubstepBuilder(type: SubstepType) {
  // During development we can have cstics defined in step, but not present in cstic list...
  if (!type) {
    return null;
  }

  let builder: CsticBuilder | null | undefined = undefined;
  // Check for special cases
  builder = SPECIAL_SUBSTEPS[type];

  // Not a special case? Build as generic tile substep
  if (!builder) {
    return DEFAULT_BUILDER;
  }
  return builder;
}

const SPECIAL_SUBSTEPS: Dict<CsticBuilder | null> = {
  [SubstepType.SystemLayout]: (data, index, total) => (
    <SystemLayoutSubstep key="system-layout" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.Length]: (data, index, total) => (
    <LengthSubstep key="length" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.LocatingFace]: (data, index, total, configuratorType) => {
    if (configuratorType === ConfiguratorType.System) {
      return <LinearLocatingFaceSubstep key="locating-face" def={data} csticIndex={index} csticAmount={total} />;
    }

    if (configuratorType === ConfiguratorType.Carriage) {
      return <CarriageLocatingFaceSubstep key="locating-face" def={data} csticIndex={index} csticAmount={total} />;
    }

    if (configuratorType === ConfiguratorType.Rail) {
      return <RailLocatingFaceSubstep key="locating-face" def={data} csticIndex={index} csticAmount={total} />;
    }

    return null;
  },
  [SubstepType.Lubrication]: (data, index, total) => (
    <LubricationSubstep key="lubrication" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.Closure]: (data, index, total) => (
    <ClosureSubstep key="closure" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.GuidewaySet]: (data, index, total) => (
    <GuidewaySetStep key="guideway-set" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.KLLTGuidewaySet]: (data, index, total) => (
    <KLLTGuidewaySetStep key="guideway-set" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.CarriageDetails]: (data, index, total) => (
    <CarriageDetailsSubstep key="carriage-details" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.KLLTCarriageDetails]: (data, index, total) => (
    <KLLTCarriageDetailsSubstep key="carriage-details" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.SummaryPage]: (data, index, total) => <ConfigurationSummarySubstep key="summary" def={data} />,

  [SubstepType.ConfigureCarriage]: (data, index, total) => (
    <ConfigureCarriageSubstep key="configure-carriage" def={data} csticIndex={index} csticAmount={total} />
  ),

  [SubstepType.ConfigureRail]: (data, index, total) => (
    <ConfigureRailSubstep key="configure-rail" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.ShaftLength]: (data, index, total) => (
    <ShaftLengthSubstep key="shaft-length" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.ShaftTolerance]: (data, index, total) => (
    <ShaftToleranceSubstep key="shaft-tolerance" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.ShaftMachining]: (data, index, total) => (
    <ShaftMachiningSubstep key="shaft-machining" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.ShaftMachiningExtra]: (data, index, total) => (
    <MetricShaftMachiningExtraSubstep key="shaft-machining-extra" def={data} csticIndex={index} csticAmount={total} />
  ),
};

const DEFAULT_BUILDER: CsticBuilder = (data, index, total) => (
  <GenericTileSubstep key={data.name} def={data} csticIndex={index} csticAmount={total} />
);
